import React, { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, FileText, AlertTriangle } from "lucide-react";
import { CREDIT_META } from "@/components/CreditDialog";

export const InvoiceView = ({ inv }) => (
    <div className="print-area bg-white p-6 text-sm text-slate-800" data-testid="invoice-view">
        <div className="flex justify-between items-start border-b pb-4 mb-4">
            <div>
                <div className="text-xl font-bold">{inv.clinic_name}</div>
                <div className="text-xs text-slate-500">e-Arşiv Fatura</div>
            </div>
            <div className="text-right text-xs">
                <div><b>Fatura No:</b> <span className="mono">{inv.number}</span></div>
                <div><b>Tarih:</b> {inv.created_at?.slice(0, 10)}</div>
                <div className="mono text-[10px] text-slate-500">ETTN: {inv.uuid}</div>
            </div>
        </div>
        <div className="mb-4">
            <div className="text-xs uppercase text-slate-500">Alıcı</div>
            <div className="font-semibold">{inv.buyer_name}</div>
            {inv.buyer_tax_id && <div>TCKN/VKN: {inv.buyer_tax_id}</div>}
            {inv.buyer_address && <div>{inv.buyer_address}</div>}
        </div>
        <table className="w-full border text-left mb-4">
            <thead className="bg-slate-50 text-xs uppercase">
                <tr><th className="p-2">Açıklama</th><th className="p-2 text-right">Matrah</th><th className="p-2 text-right">KDV %{inv.vat_rate}</th><th className="p-2 text-right">Toplam</th></tr>
            </thead>
            <tbody>
                <tr className="border-t">
                    <td className="p-2">{inv.description}</td>
                    <td className="p-2 text-right mono">{fmt(inv.net, inv.currency)}</td>
                    <td className="p-2 text-right mono">{fmt(inv.vat_amount, inv.currency)}</td>
                    <td className="p-2 text-right mono font-semibold">{fmt(inv.total, inv.currency)}</td>
                </tr>
            </tbody>
        </table>
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            <AlertTriangle className="w-3.5 h-3.5" /> {inv.gib_status} — GİB entegratörü bağlandığında resmi olarak iletilecektir.
        </div>
    </div>
);

export const InvoiceDialog = ({ inv, onClose }) => (
    <Dialog open={!!inv} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl">
            <DialogHeader className="no-print"><DialogTitle>Fatura {inv?.number}</DialogTitle></DialogHeader>
            {inv && <InvoiceView inv={inv} />}
            <Button className="dk-btn-primary no-print" onClick={() => window.print()} data-testid="invoice-print-btn">
                <Printer className="w-4 h-4 mr-2" /> Yazdır / PDF
            </Button>
        </DialogContent>
    </Dialog>
);

export default function Invoices() {
    const [items, setItems] = useState([]);
    const [credits, setCredits] = useState(null);
    const [sel, setSel] = useState(null);

    useEffect(() => {
        api.get("/invoices").then((r) => setItems(r.data));
        api.get("/clinic/credits").then((r) => setCredits(r.data));
    }, []);

    return (
        <div data-testid="invoices-page">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">E-Faturalar</h1>
                    <p className="text-slate-600 mt-1">Hasta detayında ödemelerin yanındaki "Fatura" butonu ile fatura kesebilirsiniz</p>
                </div>
                <span className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full border ${CREDIT_META.invoice.color}`} data-testid="invoice-credit-balance">
                    <FileText className="w-4 h-4" />Kalan kontür: {credits?.invoice_credits ?? 0}
                </span>
            </div>
            <Card>
                <CardContent className="p-0">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                                <th className="px-6 py-3">Fatura No</th><th className="px-6 py-3">Tarih</th><th className="px-6 py-3">Alıcı</th>
                                <th className="px-6 py-3 text-right">Tutar</th><th className="px-6 py-3">Durum</th><th className="px-6 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((i) => (
                                <tr key={i.id} className="border-b last:border-b-0" data-testid={`invoice-row-${i.id}`}>
                                    <td className="px-6 py-3 mono text-sm">{i.number}</td>
                                    <td className="px-6 py-3 text-sm">{i.created_at?.slice(0, 10)}</td>
                                    <td className="px-6 py-3 text-sm">{i.buyer_name}</td>
                                    <td className="px-6 py-3 text-right mono font-semibold">{fmt(i.total, i.currency)}</td>
                                    <td className="px-6 py-3"><span className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800">MOCK</span></td>
                                    <td className="px-6 py-3 text-right">
                                        <Button size="sm" variant="outline" onClick={() => setSel(i)} data-testid={`invoice-view-${i.id}`}>Görüntüle</Button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-slate-500 py-12">Henüz fatura yok.</td></tr>
                            )}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
            <InvoiceDialog inv={sel} onClose={() => setSel(null)} />
        </div>
    );
}
